// Scholarist membership release 2026-09-01-r2.
(() => {
  if (customElements.get('scholarist-membership-selector')) return;

  const STORAGE_KEY = 'scholarist_membership_choice';
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  class ScholaristMembershipSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      // Keep a single divider if older markup is rendered during a cart refresh.
      // The current Liquid snippet also includes the divider directly.
      if (!this.querySelector('.scholarist-membership__divider')) {
        const divider = document.createElement('hr');
        divider.className = 'scholarist-membership__divider';
        divider.setAttribute('aria-hidden', 'true');
        divider.style.cssText = 'display:block !important; width:calc(100% - 36px) !important; height:0 !important; margin:2px auto 30px !important; border:0 !important; border-top:1px solid #bdb5aa !important; opacity:1 !important; visibility:visible !important;';
        this.prepend(divider);
      }

      this.variantId = Number(this.dataset.membershipVariantId);
      this.sellingPlanId = Number(this.dataset.sellingPlanId);
      this.inputs = [...this.querySelectorAll('input[name="scholarist_membership_choice"]')];
      this.status = this.querySelector('.scholarist-membership__status');
      this.checkoutButton = document.getElementById('CartDrawer-Checkout');
      this.checkoutHandler = (event) => this.handleCheckout(event);

      this.inputs.forEach((input) => {
        input.addEventListener('change', () => this.select(input.value, true));
      });

      this.querySelector('[data-select-guest]')?.addEventListener('click', () => {
        const guest = this.querySelector('input[value="guest"]');
        if (guest) guest.checked = true;
        this.select('guest', true);
      });

      // Capture the checkout click before the cart form submits. If Member is
      // selected, add the subscription and then continue straight to checkout.
      this.checkoutButton?.addEventListener('click', this.checkoutHandler, true);

      this.initialize();
    }

    disconnectedCallback() {
      this.checkoutButton?.removeEventListener('click', this.checkoutHandler, true);
    }

    async initialize() {
      try {
        const cart = await this.getCart();
        const membershipLine = this.findMembershipLine(cart);
        const savedChoice = this.getSavedChoice();
        const initialChoice = savedChoice || this.dataset.defaultChoice || 'member';
        this.setVisualChoice(initialChoice);

        // The membership must not appear as a normal cart line. Clean up any
        // stale line left from an earlier version or an abandoned checkout.
        if (membershipLine) {
          await this.removeMembership(membershipLine.key);
        }
      } catch (error) {
        this.showError(error);
      }
    }

    async select(choice, remember) {
      if (this.getAttribute('aria-busy') === 'true') return;
      this.setVisualChoice(choice);
      if (remember) this.saveChoice(choice);

      this.setBusy(true);
      this.clearError();

      try {
        const cart = await this.getCart();
        const membershipLine = this.findMembershipLine(cart);

        // Selecting an option only changes the selector. The membership is
        // added later, at the moment the customer clicks checkout.
        if (membershipLine) {
          await this.removeMembership(membershipLine.key);
        } else {
          this.setBusy(false);
        }
      } catch (error) {
        this.setBusy(false);
        this.showError(error);

      }
    }

    async handleCheckout(event) {
      const choice = this.dataset.choice || this.dataset.defaultChoice || 'member';

      // Guest checkout follows the theme's normal form submission.
      if (choice === 'guest') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.getAttribute('aria-busy') === 'true') return;

      this.setBusy(true);
      this.clearError();
      this.checkoutButton?.setAttribute('aria-busy', 'true');

      try {
        const cart = await this.getCart();
        if (!this.findMembershipLine(cart)) {
          await this.addMembership(false);
        }

        window.location.assign(`${this.root()}checkout`);
      } catch (error) {
        this.setBusy(false);
        this.checkoutButton?.removeAttribute('aria-busy');
        this.showError(error);
      }
    }

    async addMembership(refreshCart = true) {
      this.setBusy(true);

      const payload = {
        items: [{
          id: this.variantId,
          quantity: 1,
          selling_plan: this.sellingPlanId
        }]
      };

      if (refreshCart) {
        payload.sections = SECTIONS;
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(`${this.root()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to add the membership.');

      if (refreshCart) this.refreshThemeCart(data);
      return data;
    }

    async removeMembership(lineKey) {
      this.setBusy(true);

      const response = await fetch(`${this.root()}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: SECTIONS,
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to remove the membership.');

      this.refreshThemeCart(data);
    }

    async getCart() {
      const response = await fetch(`${this.root()}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to read the cart.');
      return response.json();
    }

    findMembershipLine(cart) {
      return cart.items.find((item) => Number(item.variant_id) === this.variantId);
    }

    refreshThemeCart(data) {
      const drawer = document.querySelector('cart-drawer');

      // Elixir inherits the standard Shopify cart-drawer render method.
      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
        return;
      }

      // Safe fallback if a later theme update changes the drawer API.
      if (data.sections) {
        Object.entries(data.sections).forEach(([sectionId, html]) => {
          if (!html) return;
          const current = document.getElementById(`shopify-section-${sectionId}`);
          if (!current) return;

          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const replacement = parsed.getElementById(`shopify-section-${sectionId}`);
          if (replacement) current.replaceWith(replacement);
        });
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    }

    setVisualChoice(choice) {
      this.dataset.choice = choice;
      this.inputs.forEach((input) => {
        input.checked = input.value === choice;
      });
    }

    setBusy(isBusy) {
      this.setAttribute('aria-busy', String(isBusy));
    }

    saveChoice(choice) {
      try {
        sessionStorage.setItem(STORAGE_KEY, choice);
      } catch (_) {}
    }

    getSavedChoice() {
      try {
        return sessionStorage.getItem(STORAGE_KEY);
      } catch (_) {
        return null;
      }
    }

    showError(error) {
      if (this.status) this.status.textContent = error?.message || 'Something went wrong. Please try again.';
    }

    clearError() {
      if (this.status) this.status.textContent = '';
    }

    root() {
      return window.Shopify?.routes?.root || '/';
    }
  }

  customElements.define('scholarist-membership-selector', ScholaristMembershipSelector);
})();
